import { ZodType, ZodTypeDef } from "zod";
import { OptroImportListingJob } from "../models/audit/optroSync";
import {
  adoptServerCopy,
  isImportDone,
  newestFirst,
  toImportItem,
  TrackedOptroImport,
  trackedFromListing,
} from "./optroImportJobs";

// Cursor polling of the Optro import listing (useOptroImportJobs): the query
// for a full load or a delta, and how a delta listing merges into the tracked
// imports.

// The longest the tracker polls with a cursor before it full-loads again:
// read-time timeouts (the only thing settling a stuck import) and retiring
// finished imports happen on full loads only, and the backend has no stall
// checker.
export const FULL_RESYNC_MS = 5 * 60 * 1000;

// A full load (no cursor) carries display summaries; a delta never does.
export const listingQuery = (cursor: string | null): Record<string, string> =>
  cursor === null
    ? { includeDisplay: "true" }
    : { since: cursor, includeDisplay: "false" };

// The cursor to poll with: null (a full load) once the last full load is
// FULL_RESYNC_MS old, otherwise the cursor the last listing handed back.
export const cursorToSend = (
  cursor: string | null,
  lastFullLoadAt: number | null,
  now: number,
): string | null =>
  cursor !== null &&
  lastFullLoadAt !== null &&
  now - lastFullLoadAt >= FULL_RESYNC_MS
    ? null
    : cursor;

// Merge a delta listing (snapshot: false) into the tracked imports. A job with
// `isDone` is whole and replaces the tracked copy; one without carries only
// changed rows, merged by ref, and its completion is derived from the merged
// rows. The next poll must be a full load when the delta names a running
// import this tab hasn't seen (it came without display summaries) or sends
// partial rows for one this tab knows only from its own start request. A delta
// with no changed jobs returns `current` itself, so the caller can tell nothing
// changed.
export const mergeDeltaImports = <TSummary>(
  current: TrackedOptroImport<TSummary>[],
  changed: OptroImportListingJob[],
  summarySchema: ZodType<TSummary, ZodTypeDef, unknown>,
): { jobs: TrackedOptroImport<TSummary>[]; needsFullLoad: boolean } => {
  if (changed.length === 0) return { jobs: current, needsFullLoad: false };
  const changedById = new Map(changed.map((job) => [job.id, job]));
  const trackedById = new Map(current.map((tracked) => [tracked.id, tracked]));
  const merged = current.map((tracked) => {
    const delta = changedById.get(tracked.id);
    if (!delta) return tracked;
    if (delta.isDone !== null) {
      return adoptServerCopy(tracked, trackedFromListing(delta, summarySchema));
    }
    const rows = new Map(
      delta.items.map((item) => [item.controlExternalRef, toImportItem(item)]),
    );
    const kept = tracked.job.items.map(
      (item) => rows.get(item.controlExternalRef) ?? item,
    );
    const keptRefs = new Set(kept.map((item) => item.controlExternalRef));
    const items = [
      ...kept,
      ...[...rows.values()].filter(
        (item) => !keptRefs.has(item.controlExternalRef),
      ),
    ];
    return {
      ...tracked,
      job: { id: tracked.id, isDone: isImportDone(items), items },
    };
  });
  const added = changed
    .filter((job) => !trackedById.has(job.id) && job.isDone !== null)
    .map((job) => trackedFromListing(job, summarySchema));
  const needsFullLoad = changed.some((job) => {
    const tracked = trackedById.get(job.id);
    return tracked === undefined
      ? job.isDone !== true
      : job.isDone === null && tracked.localOnly;
  });
  return { jobs: [...added, ...merged].sort(newestFirst), needsFullLoad };
};
